import "./VideoPreview.css";

import { useEffect, useRef, useState } from "react";
import { Button, Col, Input, Row, Spin, Tooltip } from "antd";
import { EditOutlined } from "@ant-design/icons";
import { getVideoThumbnail } from "../../../../utils/GenerateVideoThumbnail";

const thumbCache = new Map();

const VideoPreview = ({
  loading,
  url,
  description,
  variant = "scene",
  isFailed = false,
  onApprove,
  onRevise,
  onRegenerate,
  approveLabel = "Approve",
  approveType = "default",
  approveDisabled = false,
  regenerateDisabled = false,
  reviseDisabled = false,
  revising = false,
  onDescriptionSave
}) => {
  const videoRef = useRef(null);
  const [reviseText, setReviseText] = useState("");
  const [posterUrl, setPosterUrl] = useState(null);
  const [posterLoading, setPosterLoading] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [editText, setEditText] = useState("");
  const [isHoveringDesc, setIsHoveringDesc] = useState(false);
  const [isSavingDesc, setIsSavingDesc] = useState(false);

  const isResult = variant === "result";
  const hasDescription = !!(description && String(description).trim().length);

  const handleEditStart = () => {
    setEditText(String(description));
    setIsEditing(true);
    setIsHoveringDesc(false);
  };

  const handleSave = async () => {
    const trimmed = editText.trim();
    if (!trimmed) return;
    setIsSavingDesc(true);
    try {
      await onDescriptionSave?.(trimmed);
      setIsEditing(false);
    } catch (e) {
      console.error(e);
    } finally {
      setIsSavingDesc(false);
    }
  };

  const handleCancel = () => {
    setIsEditing(false);
    setEditText("");
  };

  useEffect(() => {
    let cancelled = false;

    const run = async () => {
      setPosterUrl(null);
      if (!url) return;

      const cached = thumbCache.get(url);
      if (cached) {
        setPosterUrl(cached);
        return;
      }

      setPosterLoading(true);
      try {
        const thumb = await getVideoThumbnail(url, { seekTo: 0.1, width: 1280 });
        if (cancelled) return;
        thumbCache.set(url, thumb);
        setPosterUrl(thumb);
      } catch (e) {
        console.error(e);
      } finally {
        if (!cancelled) setPosterLoading(false);
      }
    };

    run();
    return () => {
      cancelled = true;
    };
  }, [url]);

  const poster = posterUrl ?? undefined;

  const PosterSpinner = posterLoading ? (
    <div className="vp-thumbLoading">
      <Spin />
    </div>
  ) : null;

  if (isResult || !hasDescription) {
    return (
      <div className="vp-resultWrap">
        <div className="vp-resultBox">
          <video
            ref={videoRef}
            className="vp-video--result"
            controls
            poster={poster}
            src={url}
            playsInline
          />
          {PosterSpinner}

          {loading && (
            <div className="vp-videoLoadingOverlay">
              <Spin />
              <span>Merging videos...</span>
            </div>
          )}
        </div>
      </div>
    );
  }


  return (
    <Row gutter={[16, 16]} className="vp-grid">
      <Col xs={24} md={12} className="vp-col">
        <div className="vp-leftPanel">
          <div className="vp-videoBox">
            <video
              ref={videoRef}
              className="vp-video"
              controls
              poster={poster}
              src={url}
              playsInline
            />
            {PosterSpinner}

            {loading && (
              <div className="vp-videoLoadingOverlay">
                <Spin />
                <span>Generating video...</span>
              </div>
            )}
          </div>

          <div className="vp-actionRow">
            <Button
              type={approveType}
              onClick={onApprove}
              disabled={approveDisabled}
            >
              {approveLabel}
            </Button>

            <Tooltip placement="bottom" title={process.env.REACT_APP_TOOLTIP_REGENERATE_VID_BUTTON}>
              <Button
                onClick={onRegenerate}
                disabled={regenerateDisabled}
                loading={loading && !revising}
              >
                Regenerate
              </Button>
            </Tooltip>
          </div>
        </div>
      </Col>

      <Col xs={24} md={12} className="vp-col">
        <div className="vp-textPanel">
          <div
            className="vp-textScroll vp-descWrapper"
            onMouseEnter={() => setIsHoveringDesc(true)}
            onMouseLeave={() => setIsHoveringDesc(false)}
          >
            {isEditing ? (
              <Input.TextArea
                value={editText}
                onChange={(e) => setEditText(e.target.value)}
                autoSize={{ minRows: 4 }}
                className="vp-descTextarea"
                autoFocus
              />
            ) : (
              <>
                <span className="vp-descText">{description}</span>
                {isHoveringDesc && !revising && (
                  <button
                    className="vp-pencilBtn"
                    onClick={handleEditStart}
                    title="Edit description"
                  >
                    <EditOutlined />
                  </button>
                )}
              </>
            )}
          </div>

          {isEditing && (
            <div className="vp-editActions">
              <Button size="small" onClick={handleCancel} disabled={isSavingDesc}>
                Cancel
              </Button>
              <Button
                type="primary"
                size="small"
                onClick={handleSave}
                loading={isSavingDesc}
                disabled={!editText.trim() || isSavingDesc}
              >
                Save
              </Button>
            </div>
          )}

          {revising && (
            <div className="vp-inlineLoading">
              <Spin size="small" />
              <span>Updating description...</span>
            </div>
          )}

          <div className="vp-reviseRow">
            <Input.TextArea
              value={reviseText}
              onChange={(e) => setReviseText(e.target.value)}
              placeholder="Revise with instructions"
              autoSize={{ minRows: 2, maxRows: 6 }}
              className="vp-reviseTextarea"
              disabled={reviseDisabled}
            />

            <Button
              type="primary"
              className="vp-reviseBtn"
              loading={revising}
              disabled={!reviseText.trim() || typeof onRevise !== "function" || reviseDisabled}
              onClick={async () => {
                const value = reviseText.trim();
                if (!value) return;

                try {
                  await onRevise(value);
                  setReviseText("");
                } catch (e) {
                  console.error(e);
                }
              }}
            >
              Revise with AI
            </Button>
          </div>
        </div>
      </Col>
    </Row>
  );
};

export default VideoPreview;